import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export const downloadPdf = async (path, filename) => {
  try {
    const r = await api.get(`${path}/pdf`, { responseType: "blob", timeout: 60000 });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) { toast.error("PDF non disponibile"); }
};

export const SendEmailButton = ({ path, filename, title = "Invia rapportino al cliente", testPrefix = "report", onSent }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(null);
    api.get(`${path}/email-defaults`).then((r) => setF({ ...r.data, to: r.data.to.join(", "), cc: "" }))
      .catch((e) => { toast.error(errorMessage(e)); setOpen(false); });
  }, [open, path]);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`${path}/send-email`, { to: split(f.to), cc: split(f.cc), subject: f.subject, message: f.message }, { timeout: 60000 });
      toast.success("Email inviata dalla tua casella Microsoft 365"); setOpen(false); onSent?.();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const tid = (s) => `${testPrefix}-${s}`;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="gap-2" data-testid={tid("send-email")}><Mail className="h-4 w-4" />Invia al cliente</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid={tid("email-dialog")}>
          <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle>
            <DialogDescription>Il PDF viene allegato e inviato dalla tua casella Microsoft 365 (lo trovi in Posta inviata).</DialogDescription></DialogHeader>
          {!f ? <p className="py-6 text-sm text-slate-500">Caricamento…</p> : (
            <div className="space-y-3">
              {!f.outlook_connected && <p className="rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={tid("email-not-connected")}>Account Microsoft 365 non collegato: esci e rientra con Microsoft per poter inviare.</p>}
              {f.outlook_connected && !f.mail_enabled && <p className="rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={tid("email-no-permission")}>Permesso di invio email non ancora concesso: esci e rientra con Microsoft una volta per autorizzarlo.</p>}
              {f.note && <p className="rounded-control bg-brand-50 px-3 py-2 text-xs text-brand-800" data-testid={tid("email-note")}>{f.note}</p>}
              <div className="space-y-1.5"><Label className="text-xs">A (separa più indirizzi con virgola)</Label><Input value={f.to} onChange={set("to")} placeholder="referente@cliente.it" data-testid={tid("email-to")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Cc (facoltativo)</Label><Input value={f.cc} onChange={set("cc")} data-testid={tid("email-cc")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Oggetto</Label><Input value={f.subject} onChange={set("subject")} maxLength={200} data-testid={tid("email-subject")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Messaggio</Label><Textarea rows={6} value={f.message} onChange={set("message")} maxLength={5000} data-testid={tid("email-message")} /></div>
              <p className="text-xs text-slate-500">Allegato: {f.filename || filename}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={send} disabled={busy || !f || !split(f.to).length || !f.subject.trim() || !f.outlook_connected || !f.mail_enabled} className="gap-2" data-testid={tid("email-send")}><Mail className="h-4 w-4" />{busy ? "Invio…" : "Invia"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
